<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <div v-if="pageLoading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <!-- Detail Header -->
      <div class="detail-header">
        <div class="detail-icon" :class="providerIconClass">
          <img
            v-if="providerLogo"
            :src="providerLogo"
            style="width: 26px; height: 26px;"
          />
          <i v-else :class="providerIcon" style="font-size: 26px;"></i>
        </div>
        <div class="detail-info">
          <div class="detail-name">
            {{ form.name || 'Integração' }}
            <span
              class="int-badge"
              :class="form.is_active ? 'active-badge' : 'inactive-badge'"
            >
              <template v-if="form.is_active">&#9679; Ativo</template>
              <template v-else>Desativado</template>
            </span>
          </div>
          <div class="detail-meta">
            <span class="detail-meta-item">
              <i class="bx bx-spreadsheet" style="font-size: 14px; opacity: 0.5;"></i>
              {{ providerName }}
            </span>
            <span v-if="form.sync_direction !== 'pull'" class="detail-meta-item">
              <i class="bx bx-time-five" style="font-size: 14px; opacity: 0.5;"></i>
              {{ frequencyLabel(form.sync_frequency) }}
            </span>
            <span v-if="form.sync_direction === 'pull'" class="detail-meta-item">
              <i class="bx bx-time-five" style="font-size: 14px; opacity: 0.5;"></i>
              Automático
            </span>
            <span v-if="filterSummary" class="detail-meta-item">
              <i class="bx bx-filter-alt" style="font-size: 14px; opacity: 0.5;"></i>
              {{ filterSummary }}
            </span>
            <span v-if="form.sync_direction !== 'pull'" class="detail-meta-item">
              <i class="bx bx-transfer" style="font-size: 14px; opacity: 0.5;"></i>
              {{ form.field_mappings.length }} campos
            </span>
          </div>
        </div>
        <div class="detail-actions">
          <label class="toggle-switch">
            <input type="checkbox" :checked="form.is_active" @change="doToggle" />
            <span class="toggle-slider"></span>
          </label>
          <button
            class="di-btn di-btn-success di-btn-sm"
            :disabled="syncing"
            @click="doSync"
          >
            <i class="bx" :class="syncing ? 'bx-loader-alt bx-spin' : 'bx-sync'" style="font-size: 16px;"></i>
            {{ syncing ? 'Sincronizando...' : 'Sync agora' }}
          </button>
          <button class="di-btn-icon" @click="showWizard = true" title="Configurar">
            <i class="bx bx-cog" style="font-size: 18px;"></i>
          </button>
          <button
            class="di-btn-icon"
            style="color: #f46a6a; border-color: #fecaca;"
            title="Excluir"
            @click="doDelete"
          >
            <i class="bx bx-trash" style="font-size: 18px;"></i>
          </button>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-card-label">Total Sincronizados</div>
          <div class="stat-card-value">{{ stats.totalRows }}</div>
          <div class="stat-card-sub">registros na planilha</div>
        </div>
        <div class="stat-card">
          <div class="stat-card-label">Última Sincronização</div>
          <div class="stat-card-value" :style="{ color: syncStatusColor(form.last_sync_status) }">
            {{ syncStatusLabel(form.last_sync_status) }}
          </div>
          <div class="stat-card-sub">{{ stats.lastSyncSub }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-card-label">Syncs Hoje</div>
          <div class="stat-card-value">{{ stats.syncsToday }}</div>
          <div class="stat-card-sub">{{ stats.syncsTodaySub }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-card-label">Taxa de Sucesso</div>
          <div class="stat-card-value">{{ stats.successRate }}</div>
          <div class="stat-card-sub">últimos registros</div>
        </div>
      </div>

      <!-- Activity Card -->
      <div class="activity-card">
        <div class="activity-card-header">
          <div class="activity-card-title">
            <i class="bx bx-history" style="font-size: 18px; color: #8d92a9;"></i>
            Atividade
          </div>
        </div>
        <div class="activity-card-body">
          <div v-if="logsLoading" class="text-center py-4">
            <b-spinner variant="primary" small></b-spinner>
          </div>

          <div v-else-if="!logs.length" class="text-center py-5">
            <i class="bx bx-history" style="font-size: 48px; color: #b8bdd0;"></i>
            <p style="margin-top: 8px; color: #8d92a9; font-size: 13px;">Nenhuma sincronização realizada.</p>
          </div>

          <template v-else>
            <div
              v-for="log in logs"
              :key="log.id"
              class="timeline-item"
            >
              <div class="timeline-dot-col">
                <div
                  class="timeline-dot"
                  :class="logDotClass(log.status)"
                ></div>
              </div>
              <div class="timeline-content">
                <div class="timeline-title">
                  {{ logTitle(log.status) }}
                  <span class="tl-badge" :class="logBadgeClass(log.status)">
                    {{ logBadgeLabel(log.status) }}
                  </span>
                </div>
                <div class="timeline-meta">
                  <span v-if="log.rows_synced != null" class="timeline-meta-item">
                    <i class="bx bx-data" style="font-size: 14px; opacity: 0.5;"></i>
                    {{ log.rows_synced }} {{ form.sync_direction === 'pull' ? 'confirmados' : 'registros' }}
                  </span>
                  <span v-if="log.duration_ms" class="timeline-meta-item">
                    <i class="bx bx-timer" style="font-size: 14px; opacity: 0.5;"></i>
                    {{ (log.duration_ms / 1000).toFixed(1) }}s
                  </span>
                  <span v-if="log.details && log.details.skipped_duplicates" class="timeline-meta-item">
                    <i class="bx bx-skip-next-circle" style="font-size: 14px; opacity: 0.5;"></i>
                    {{ log.details.skipped_duplicates }} ignorados
                  </span>
                  <span v-if="log.details && log.details.already_confirmed" class="timeline-meta-item">
                    <i class="bx bx-check-double" style="font-size: 14px; opacity: 0.5;"></i>
                    {{ log.details.already_confirmed }} já confirmados
                  </span>
                  <span v-if="log.details && log.details.pending_sympla" class="timeline-meta-item">
                    <i class="bx bx-time-five" style="font-size: 14px; opacity: 0.5;"></i>
                    {{ log.details.pending_sympla }} pendentes na Sympla
                  </span>
                </div>
                <div v-if="log.error_message" class="timeline-error-msg">
                  {{ log.error_message }}
                </div>
              </div>
              <div class="timeline-right">
                <div class="timeline-time">{{ formatTime(log.created_at) }}</div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Wizard Modal -->
    <WizardModal
      :show="showWizard"
      :form="form"
      :providers="providers"
      :availableFields="availableFields"
      :isEditing="true"
      :saving="saving"
      :testing="testing"
      :testResult="testResult"
      :testError="testError"
      @save="saveForm"
      @test="doTestConnection"
      @close="showWizard = false"
    />
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import WizardModal from './components/WizardModal.vue'
import services from './services'
import groupService from '@/pages/groups/services'

export default {
  components: { Layout, PageHeader, WizardModal },
  name: 'data-integration-form',
  page: {
    title: 'Integração de Dados',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      pageLoading: true,
      showWizard: false,
      saving: false,
      syncing: false,
      testing: false,
      testResult: null,
      testError: '',
      logsLoading: false,
      logs: [],
      groups: [],
      form: {
        id: null,
        name: '',
        provider: '',
        credentials: null,
        config: {},
        sync_frequency: 'manual',
        filters: { statuses: [], group_ids: [], tag_ids: [] },
        sync_direction: 'push',
        is_active: true,
        field_mappings: [],
        last_synced_at: null,
        last_sync_status: null,
        last_sync_rows: null
      },
      title: 'Integração',
      breadcrumbs: [
        { text: 'Configurações', href: '/' },
        { text: 'Integrações', href: '/settings/data-integrations' },
        { text: '', href: '' },
        { text: 'Detalhe', active: true }
      ],
      providerMeta: {
        google_sheets: {
          name: 'Google Sheets',
          logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ae/Google_Sheets_2020_Logo.svg',
          iconBg: '#e8f5e9',
          iconColor: '#0f9d58',
          iconClass: 'sheets'
        },
        sympla: {
          name: 'Sympla',
          logo: null,
          iconBg: '#eef0ff',
          iconColor: '#4253f0',
          iconClass: 'sympla'
        },
        eventfy_app: {
          name: 'App Eventfy',
          logo: null,
          iconBg: '#eef0ff',
          iconColor: '#6d4aff',
          iconClass: 'eventfy'
        }
      },
      statusLabels: {
        0: 'A Definir',
        1: 'Em Andamento',
        2: 'Pendente',
        3: 'Confirmado',
        4: 'Convidado'
      }
    }
  },
  computed: {
    ...mapState('dataIntegrations', ['providers', 'availableFields', 'integration']),
    providerName () {
      const meta = this.providerMeta[this.form.provider]
      return meta ? meta.name : this.form.provider
    },
    providerLogo () {
      const meta = this.providerMeta[this.form.provider]
      return meta ? meta.logo : null
    },
    providerIconClass () {
      const meta = this.providerMeta[this.form.provider]
      return meta ? meta.iconClass : ''
    },
    providerIcon () {
      if (this.form.provider === 'sympla') return 'mdi mdi-alpha-s-box'
      if (this.form.provider === 'eventfy_app') return 'bx bx-mobile-alt'
      return 'bx bx-plug'
    },
    filterSummary () {
      const parts = []
      const statuses = this.form.filters.statuses || []
      if (statuses.length) {
        const labels = statuses.map(s => this.statusLabels[s] || s)
        parts.push('Status: ' + labels.join(', '))
      }
      const groupIds = this.form.filters.group_ids || []
      if (groupIds.length && this.groups.length) {
        const groupNames = groupIds.map(id => {
          const g = this.groups.find(g => g.id === id)
          return g ? g.name : id
        })
        parts.push('Grupos: ' + groupNames.join(', '))
      }
      return parts.join(' · ')
    },
    stats () {
      const totalRows = this.form.last_sync_rows || 0

      // Last sync sub
      let lastSyncSub = '—'
      if (this.form.last_synced_at) {
        lastSyncSub = this.timeAgo(this.form.last_synced_at)
        const lastLog = this.logs.length ? this.logs[0] : null
        if (lastLog && lastLog.duration_ms) {
          lastSyncSub += ' · ' + (lastLog.duration_ms / 1000).toFixed(1) + 's'
        }
      }

      // Syncs today
      const today = new Date().toDateString()
      const todayLogs = this.logs.filter(l => {
        try { return new Date(l.created_at).toDateString() === today } catch (e) { return false }
      })
      const syncsToday = todayLogs.length
      const failsToday = todayLogs.filter(l => l.status === 'error').length
      const okToday = syncsToday - failsToday
      let syncsTodaySub = '—'
      if (syncsToday > 0) {
        syncsTodaySub = failsToday > 0
          ? `${failsToday} falha${failsToday > 1 ? 's' : ''} · ${okToday} ok`
          : `${okToday} ok`
      }

      // Success rate
      const totalLogs = this.logs.length
      const successLogs = this.logs.filter(l => l.status === 'success').length
      const successRate = totalLogs > 0
        ? ((successLogs / totalLogs) * 100).toFixed(1) + '%'
        : '—'

      return { totalRows, lastSyncSub, syncsToday, syncsTodaySub, successRate }
    }
  },
  async mounted () {
    const id = this.$route.params.id
    if (!id) {
      this.$router.replace({ name: 'data-integrations' })
      return
    }

    let loader = this.$loading.show()
    try {
      await Promise.all([
        this.fetchProviders(),
        this.fetchAvailableFields(),
        this.fetchIntegration(id),
        this.fetchGroups()
      ])
      this.populateForm()
      this.loadLogs()
      const provMeta = this.providerMeta[this.form.provider]
      this.breadcrumbs[2] = {
        text: provMeta ? provMeta.name : this.form.provider,
        href: '/settings/data-integrations/' + this.form.provider
      }
      this.breadcrumbs[3] = { text: this.form.name || 'Detalhe', active: true }
    } catch (err) {
      errorMessage('Falha ao carregar integração.')
    }
    this.pageLoading = false
    loader.hide()
  },
  methods: {
    ...mapActions('dataIntegrations', {
      fetchProviders: 'GetProviders',
      fetchAvailableFields: 'GetAvailableFields',
      fetchIntegration: 'GetIntegration',
      saveIntegration: 'SaveIntegration',
      toggleStatus: 'ToggleStatus',
      triggerSync: 'TriggerSync',
      testConnection: 'TestConnection',
      removeIntegration: 'RemoveIntegration'
    }),
    async fetchGroups () {
      try {
        const res = await groupService.list()
        this.groups = res.data.data || []
      } catch (e) {
        this.groups = []
      }
    },
    populateForm () {
      const d = this.integration
      this.form = {
        id: d.id,
        name: d.name || '',
        provider: d.provider || '',
        credentials: d.credentials || null,
        config: d.config || {},
        sync_frequency: d.sync_frequency || 'manual',
        filters: d.filters || { statuses: [], group_ids: [], tag_ids: [] },
        sync_direction: d.sync_direction || 'push',
        is_active: d.is_active !== undefined ? d.is_active : true,
        field_mappings: (d.field_mappings || []).map(m => ({
          source_type: m.source_type,
          source_key: m.source_key,
          target_column: m.target_column,
          sequence: m.sequence
        })),
        last_synced_at: d.last_synced_at || null,
        last_sync_status: d.last_sync_status || null,
        last_sync_rows: d.last_sync_rows || null
      }
    },
    async saveForm () {
      this.saving = true
      try {
        await this.saveIntegration(this.form)
        successMessage('Integração salva com sucesso.', 2500)
        this.showWizard = false
        await this.fetchIntegration(this.form.id)
        this.populateForm()
      } catch (err) {
        errorMessage('Falha ao salvar: ' + (err.message || err.error || 'Erro desconhecido'))
      }
      this.saving = false
    },
    async doToggle () {
      try {
        await this.toggleStatus(this.form.id)
        this.form.is_active = !this.form.is_active
        successMessage(this.form.is_active ? 'Integração ativada.' : 'Integração desativada.', 2000)
      } catch (err) {
        errorMessage('Falha ao alterar status.')
      }
    },
    async doSync () {
      this.syncing = true
      try {
        await this.triggerSync(this.form.id)
        successMessage('Sincronização executada com sucesso.', 2500)
        this.loadLogs()
        await this.fetchIntegration(this.form.id)
        this.populateForm()
      } catch (err) {
        errorMessage('Falha na sincronização: ' + (err.message || err.error || 'Erro desconhecido'))
        this.loadLogs()
      }
      this.syncing = false
    },
    async doDelete () {
      if (!confirm('Deseja realmente excluir esta integração?')) return
      try {
        await this.removeIntegration(this.form.id)
        successMessage('Integração removida.', 2000)
        this.$router.push({ name: 'data-integration-provider', params: { provider: this.form.provider } })
      } catch (err) {
        errorMessage('Falha ao excluir.')
      }
    },
    async doTestConnection () {
      this.testing = true
      this.testResult = null
      this.testError = ''
      try {
        const res = await this.testConnection(this.form.id)
        this.testResult = res.data.success
        if (!res.data.success) {
          this.testError = res.data.message || 'Falha'
        }
      } catch (err) {
        this.testResult = false
        this.testError = err.message || 'Falha na conexão'
      }
      this.testing = false
    },
    async loadLogs () {
      if (!this.form.id) return
      this.logsLoading = true
      try {
        const res = await services.syncLogs(this.form.id, { per_page: 30 })
        this.logs = res.data.data || res.data || []
      } catch (err) {
        // silent
      }
      this.logsLoading = false
    },
    frequencyLabel (freq) {
      if (!freq || freq === 'manual') return 'Manual'
      if (freq === 'hourly') return 'A cada 1h'
      if (freq === 'daily') return 'Diária'
      if (freq.startsWith('daily:')) return 'Diária às ' + freq.substring(6)
      const hMatch = freq.match(/^(\d+)h$/)
      if (hMatch) return 'A cada ' + hMatch[1] + 'h'
      const mMatch = freq.match(/^(\d+)min$/)
      if (mMatch) return 'A cada ' + mMatch[1] + ' min'
      return freq
    },
    syncStatusColor (status) {
      return {
        success: '#34c38f',
        error: '#f46a6a',
        partial: '#f1b44c',
        started: '#2563eb'
      }[status] || '#8d92a9'
    },
    syncStatusLabel (status) {
      return {
        success: 'Sucesso',
        error: 'Erro',
        partial: 'Parcial',
        started: 'Rodando...'
      }[status] || '—'
    },
    timeAgo (dateStr) {
      if (!dateStr) return '—'
      const diff = Date.now() - new Date(dateStr).getTime()
      const minutes = Math.floor(diff / 60000)
      if (minutes < 1) return 'agora'
      if (minutes < 60) return `há ${minutes} min`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `há ${hours}h`
      const days = Math.floor(hours / 24)
      return `há ${days} dia${days > 1 ? 's' : ''}`
    },
    formatTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const today = new Date()
      if (d.toDateString() === today.toDateString()) {
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      }
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' +
        d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    logDotClass (status) {
      return { success: 'success', error: 'error', started: 'running', partial: 'error' }[status] || 'success'
    },
    logTitle (status) {
      return {
        success: 'Sincronização concluída',
        error: 'Falha na sincronização',
        started: 'Sincronizando...',
        partial: 'Sincronização parcial'
      }[status] || 'Sincronização'
    },
    logBadgeClass (status) {
      return { success: 'success', error: 'error', started: 'running', partial: 'error' }[status] || 'success'
    },
    logBadgeLabel (status) {
      return {
        success: 'Sucesso',
        error: 'Erro',
        started: 'Em andamento',
        partial: 'Parcial'
      }[status] || status
    }
  }
}
</script>

<style scoped>
/* Detail Header */
.detail-header {
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  align-items: center;
  gap: 16px;
}
.detail-icon {
  width: 44px; height: 44px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.detail-icon.sheets { background: #e8f5e9; color: #0f9d58; }
.detail-icon.sympla { background: #eef0ff; color: #4253f0; }
.detail-icon.eventfy { background: #eef0ff; color: #6d4aff; }
.detail-info { flex: 1; }
.detail-name {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.detail-meta {
  font-size: 12px;
  color: #8d92a9;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.detail-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
.detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.detail-actions .toggle-switch {
  display: flex;
  align-items: center;
}

/* Badges */
.int-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.int-badge.active-badge { background: #d4f5e7; color: #0d7a5f; }
.int-badge.inactive-badge {
  background: #f8f9fb;
  color: #8d92a9;
  border: 1px solid #edf0f5;
}

/* Toggle */
.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  display: inline-block;
  cursor: pointer;
}
.toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #e2e5ed;
  border-radius: 100px;
  transition: 0.25s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px; width: 18px;
  left: 3px; bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.25s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.toggle-switch input:checked + .toggle-slider { background: #34c38f; }
.toggle-switch input:checked + .toggle-slider::before { transform: translateX(20px); }

/* Custom Buttons (prefixed to avoid Bootstrap conflicts) */
.di-btn {
  padding: 8px 18px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
}
.di-btn-sm { padding: 6px 14px; font-size: 12px; }
.di-btn-success { background: #34c38f; color: #fff; }
.di-btn-success:hover { background: #2ab07d; }
.di-btn-success:disabled { opacity: 0.6; cursor: not-allowed; }
.di-btn-icon {
  width: 36px; height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e2e5ed;
  color: #8d92a9;
  cursor: pointer;
  transition: all 0.15s;
}
.di-btn-icon:hover { background: #f8f9fb; color: #4d5269; }

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.stat-card {
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
}
.stat-card-label {
  font-size: 11px;
  font-weight: 500;
  color: #8d92a9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.stat-card-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1;
}
.stat-card-sub {
  font-size: 11px;
  color: #8d92a9;
  margin-top: 4px;
}

/* Activity Card */
.activity-card {
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}
.activity-card-header {
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf0f5;
}
.activity-card-title {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Timeline */
.timeline-item {
  display: flex;
  gap: 14px;
  padding: 18px 24px;
  transition: background 0.12s;
}
.timeline-item:not(:last-child) { border-bottom: 1px solid #edf0f5; }
.timeline-item:hover { background: #f8f9fb; }
.timeline-dot-col {
  display: flex;
  align-items: flex-start;
  width: 20px;
  flex-shrink: 0;
  padding-top: 4px;
}
.timeline-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
}
.timeline-dot.success { background: #34c38f; box-shadow: 0 0 0 3px #d4f5e7; }
.timeline-dot.error { background: #f46a6a; box-shadow: 0 0 0 3px #fde8e8; }
.timeline-dot.running {
  background: #2563eb;
  box-shadow: 0 0 0 3px #eff4ff;
  animation: pulse-tl 2s ease-in-out infinite;
}
@keyframes pulse-tl {
  0%, 100% { box-shadow: 0 0 0 3px #eff4ff; }
  50% { box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.1); }
}
.timeline-content { flex: 1; min-width: 0; }
.timeline-title {
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.tl-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 5px;
}
.tl-badge.success { background: #d4f5e7; color: #0d7a5f; }
.tl-badge.error { background: #fde8e8; color: #b91c1c; }
.tl-badge.running { background: #eff4ff; color: #2563eb; }
.timeline-meta {
  font-size: 12px;
  color: #8d92a9;
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}
.timeline-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
.timeline-error-msg {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fde8e8;
  border: 1px solid #fecaca;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  color: #991b1b;
}
.timeline-right { flex-shrink: 0; }
.timeline-time {
  font-size: 11px;
  color: #b8bdd0;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
}

@media (max-width: 900px) {
  .stats-row { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .stats-row { grid-template-columns: 1fr; }
  .detail-header { flex-wrap: wrap; }
  .detail-actions { width: 100%; justify-content: flex-end; }
}
</style>
